import { useList, useDelete } from '@refinedev/core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Pencil, Trash2, RadioTower } from 'lucide-react';
import { LivestreamStatus, LivestreamVisibility } from '@masters-club/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormHeader } from '@/components/forms';
import { notify } from '@/lib/notify';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import {
  DataTable,
  TableToolbar,
  type Column,
  type SortState,
  type BulkAction,
} from '@/components/tables';
import {
  type Livestream,
  LIVESTREAM_STATUS_META,
  LIVESTREAM_VISIBILITY_LABELS,
  getLivestreamTitle,
} from './types';

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

function LivestreamStatusBadge({ status }: { status: LivestreamStatus }) {
  const meta = LIVESTREAM_STATUS_META[status];
  return (
    <Badge variant={meta.variant} className="gap-1.5 font-normal">
      <span className={`inline-block h-2 w-2 rounded-full ${meta.dotClass}`} />
      {meta.label}
    </Badge>
  );
}

export function LivestreamsList() {
  useScrollRestoration();
  const navigate = useNavigate();
  const [page, setPage] = usePersistedState('livestreams:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('livestreams:sort', { field: 'scheduledAt', order: 'desc' });
  const [statusFilter, setStatusFilter] = usePersistedState<LivestreamStatus | 'all'>('livestreams:statusFilter', 'all');
  const [visibilityFilter, setVisibilityFilter] = usePersistedState<LivestreamVisibility | 'all'>('livestreams:visibilityFilter', 'all');
  const [search, setSearch] = usePersistedState('livestreams:search', '');
  const [deleteItems, setDeleteItems] = useState<Livestream[]>([]);
  const pageSize = 50;

  const filters: Array<{ field: string; operator: 'eq' | 'contains'; value: string }> = [];
  if (statusFilter !== 'all') {
    filters.push({ field: 'status', operator: 'eq', value: statusFilter });
  }
  if (visibilityFilter !== 'all') {
    filters.push({ field: 'visibility', operator: 'eq', value: visibilityFilter });
  }
  if (search.trim()) {
    filters.push({ field: 'search', operator: 'contains', value: search.trim() });
  }

  const { result, query } = useList<Livestream>({
    resource: 'livestreams',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
    filters,
  });

  const allItems = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query?.isLoading ?? false;

  const { mutate: deleteLivestream, mutation: deleteMutation } = useDelete();
  const isDeleting = deleteMutation.isPending;

  const handleDelete = () => {
    if (deleteItems.length === 0) {
      return;
    }
    const count = deleteItems.length;
    for (const item of deleteItems) {
      deleteLivestream({
        resource: 'livestreams',
        id: item.id,
        successNotification: false,
      });
    }
    notify.success(`Удалено: ${count}`);
    setDeleteItems([]);
  };

  const columns: Column<Livestream>[] = [
    {
      key: 'title',
      header: 'Название',
      sortable: true,
      render: (_, row) => {
        const { title, isFallback } = getLivestreamTitle(row);
        return (
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center">
              <RadioTower className="h-4 w-4" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium text-sm flex items-center gap-2">
                {title}
                {isFallback && (
                  <Badge variant="outline" className="text-[10px] font-normal">
                    fallback
                  </Badge>
                )}
              </span>
              <span className="text-xs text-muted-foreground">#{row.id}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'languages',
      header: 'Языки',
      render: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {(row.languages ?? []).map((code) => (
            <Badge key={code} variant="outline" className="text-[10px] font-normal uppercase">
              {code}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      sortable: true,
      render: (_, row) => <LivestreamStatusBadge status={row.status} />,
    },
    {
      key: 'visibility',
      header: 'Видимость',
      sortable: true,
      render: (_, row) => (
        <span className="text-sm">{LIVESTREAM_VISIBILITY_LABELS[row.visibility]}</span>
      ),
    },
    {
      key: 'scheduledAt',
      header: 'Запланирована',
      sortable: true,
      render: (val) => formatDateTime(typeof val === 'string' ? val : null),
    },
    {
      key: 'peakViewers',
      header: 'Пик зрителей',
      sortable: true,
      render: (_, row) => (
        <span className="text-sm tabular-nums">{row.peakViewers}</span>
      ),
    },
    {
      key: 'hostAdminId',
      header: 'Хост',
      sortable: true,
      render: (_, row) => <span className="text-sm text-muted-foreground">#{row.hostAdminId}</span>,
    },
  ];

  const bulkActions: BulkAction<Livestream>[] = [
    {
      label: 'Открыть',
      icon: <Eye className="h-4 w-4" />,
      onClick: (rows) => {
        if (rows.length === 1) {
          navigate(`/livestreams/show/${rows[0].id}`);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Редактировать',
      icon: <Pencil className="h-4 w-4" />,
      onClick: (rows) => {
        if (rows.length === 1) {
          navigate(`/livestreams/edit/${rows[0].id}`);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Прямые трансляции"
        actions={(
          <Button onClick={() => navigate('/livestreams/create')}>
            <Plus className="h-4 w-4 mr-2" />
            Создать
          </Button>
        )}
        sticky
      />

      <div className="flex flex-wrap items-center gap-4">
        <TableToolbar
          searchValue={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          searchPlaceholder="Поиск по названию..."
        />
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v as LivestreamStatus | 'all');
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            {Object.values(LivestreamStatus).map((status) => (
              <SelectItem key={status} value={status}>
                {LIVESTREAM_STATUS_META[status].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={visibilityFilter}
          onValueChange={(v) => {
            setVisibilityFilter(v as LivestreamVisibility | 'all');
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Видимость" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Любая видимость</SelectItem>
            {Object.values(LivestreamVisibility).map((v) => (
              <SelectItem key={v} value={v}>
                {LIVESTREAM_VISIBILITY_LABELS[v]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={allItems}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(row) => navigate(`/livestreams/show/${row.id}`)}
        emptyMessage="Трансляций нет"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? 'Удалить трансляцию?'
                : `Удалить ${deleteItems.length} трансляций?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Трансляция и связанный с ней чат будут удалены безвозвратно. Активную трансляцию (LIVE) удалить нельзя — сначала завершите или отмените её.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
